import React from "react";
import { Modal, Tooltip, Button, message } from "antd";
import {
  workflowThreeById,
  workflowThreeErrorById,
} from "../../../../libs/apis";
import { WorkflowThreeI } from "../../../../libs/interfaces";
import { Box, Grid } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  CloseOutlined,
  FundViewOutlined,
} from "@ant-design/icons";
import { OneCard, OneVideo } from "../../../../libs/shared";

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type: "naviera" | "exporter" | "other" | "gps" | "enginery" | "video";
}

interface ShowContainerPropsI {
  id: number;
  isError?: boolean;
}

export const ShowContainer = ({ id, isError }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };
  
  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer
          open={open}
          onClose={handleClose}
          id={id}
          isError={isError}
        />
      )}
    </>
  );
};

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
  isError?: boolean;
}

export const ModalContainer = ({
  open,
  onClose,
  id,
  isError,
}: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<WorkflowThreeI>({
    container: "",
  } as WorkflowThreeI);
  const [cards, setCards] = React.useState<OneCardI[]>([]);
  const [cardVideos, setCardVideos] = React.useState<OnePhotoI[]>([]);
  const [totalPhotos, setTotalPhotos] = React.useState<string[]>([]);

  React.useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as WorkflowThreeI);
      setLoading(true);
    };
  }, []);

  const handleGetData = async () => {
    try {
      let dataContainer: WorkflowThreeI;
      if (isError) {
        const { data: dataContainerError } = await workflowThreeErrorById(id);
        if (!dataContainerError) return;
        dataContainer = dataContainerError;
        setData(dataContainer);
      } else {
        const { data: dataContainerGet } = await workflowThreeById(id);
        if (!dataContainerGet) return;
        dataContainer = dataContainerGet;
        setData(dataContainerGet);
      }

      const lasFotos: string[] = [];
      const arrCards: OneCardI[] = [
        {
          title: "Foto Panorámica",
          photos: [
            {
              src: dataContainer.containerPanoramicPhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.containerPanoramicCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.containerPanoramicComment,
          type: "naviera",
        },
        {
          title: "Foto Botella Naviera",
          photos: [
            {
              src: dataContainer.navieraBottlePhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.navieraBottleCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.navieraBottleComment,
          type: "naviera",
        },
        {
          title: "Foto Etiqueta Naviera",
          photos: [
            {
              src: dataContainer.navieraLabelPhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.navieraLabelCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.navieraLabelComment,
          type: "naviera",
        },
        {
          title: "Foto Cable Naviera",
          photos: [
            {
              src: dataContainer.navieraWirePhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.navieraWireCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.navieraWireComment,
          type: "naviera",
        },
        {
          title: "Foto Botella Exportador",
          photos: [
            {
              src: dataContainer.exporterBottlePhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.exporterBottleCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.exporterBottleComment,
          type: "exporter",
        },
        {
          title: "Foto Etiqueta Exportador",
          photos: [
            {
              src: dataContainer.exporterLabelPhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.exporterLabelCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.exporterLabelComment,
          type: "exporter",
        },
        {
          title: "Foto Cable Exportador",
          photos: [
            {
              src: dataContainer.exporterWirePhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.exporterWireCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.exporterWireComment,
          type: "exporter",
        },
        {
          title: "Foto Botella Otro",
          photos: [
            {
              src: dataContainer.otherBottlePhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.otherBottleCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.otherBottleComment,
          type: "other",
        },
        {
          title: "Foto Etiqueta Otro",
          photos: [
            {
              src: dataContainer.otherLabelPhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.otherLabelCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.otherLabelComment,
          type: "other",
        },
        {
          title: "Foto Cable Otro",
          photos: [
            {
              src: dataContainer.otherWirePhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.otherWireCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.otherWireComment,
          type: "other",
        },
        {
          title: "Foto Gps",
          photos: [
            {
              src: dataContainer.gpsPhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.gpsCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.gpsComment,
          type: "gps",
        },
        {
          title: "Foto Sello Gps",
          photos: [
            {
              src: dataContainer.gpsStampPhoto,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.gpsStampCheck,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.gpsStampComment,
          type: "gps",
        },
        {
          title: "Foto Maquinaria 1",
          photos: [
            {
              src: dataContainer.engineryPhoto1,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.engineryCheck1,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.engineryComment1,
          type: "enginery",
        },
        {
          title: "Foto Maquinaria 2",
          photos: [
            {
              src: dataContainer.engineryPhoto2,
              alt: "Validación",
              showAlt: true,
            },
            {
              src: dataContainer.engineryCheck2,
              alt: "Proceso 2",
              showAlt: true,
            },
          ],
          value: dataContainer.engineryComment2,
          type: "enginery",
        },
      ];

      arrCards.forEach((photo) => {
        // if(photo.photos.length > 1) {
        photo.photos.forEach((photo) => {
          lasFotos.push(photo.src as string);
        });
      });
      const arrVideos: OnePhotoI[] = [
        { src: dataContainer.engineryVideo, alt: "Video Maquinaria" },
        { src: dataContainer.doorVideo, alt: "Video Puerta" },
      ];

      setTotalPhotos(lasFotos);
      setCardVideos(arrVideos);
      setCards(arrCards);
      setLoading(false);
    } catch (err: any) {
      message.error((err as Error).message);
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <p
          style={{
            color: "white",
            padding: "10px",
            fontWeight: "bold",
            fontSize: "1.2rem",
            textAlign: "center",
            background: "linear-gradient(to bottom, #FFA500, #FF4500)", // Naranja a rojo
            borderRadius: "5px",
            width: "98%",
            margin: "0 auto",
          }}
        >
          {`CONTENEDOR N° ${data.container}`}
        </p>
      }
      closeIcon={
        <CloseOutlined
          style={{
            background: "red",
            color: "white",
            padding: "5px",
            borderRadius: "5px",
          }}
        />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={810}
      centered={true}
      zIndex={1602}
      destroyOnClose={true}
      style={{ padding: 0 }}
    >
      <Box sx={{ overflowY: "auto", maxHeight: 550, p: 1 }}>
        <PhotoSection
          title="Naviera"
          type="naviera"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Exportador"
          type="exporter"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Otras"
          type="other"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Gps"
          type="gps"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Maquinaria"
          type="enginery"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        {/* <VideoSection videos={cardVideos} /> */}
      </Box>
    </Modal>
  );
};

const PhotoSection = ({
  title,
  type,
  cards,
  totalPhotos,
}: {
  title: string;
  type: string;
  cards: OneCardI[];
  totalPhotos: string[];
}) => (
  <>
    <Box
      sx={{
        textAlign: "center",
        mb: 2,
        mt: 4,
        bgcolor: "transparent",
        borderRadius: "4px",
        background:
          "linear-gradient(to bottom, #A9A9A9 0%,#14301A 50%,rgb(29, 68, 152) 100%)",
      }}
    >
      <h3 style={{ color: "white" }}>{title}</h3>
    </Box>
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      }}
    >
      {cards
        .filter((el) => el.type === type)
        .map((el) => (
          <OneCard data={el} key={el.title} lasFotos={totalPhotos} />
        ))}
    </Box>
  </>
);

const VideoSection = ({ videos }: { videos: OnePhotoI[] }) => (
  <>
    <Box
      sx={{
        textAlign: "center",
        mb: 4,
        mt: 8,
        bgcolor: "transparent",
        borderRadius: "4px",
        background:
          "linear-gradient(to bottom, #A9A9A9 0%,#14301A 50%,rgb(29, 68, 152) 100%)",
      }}
    >
      <h3 style={{ color: "white" }}>Videos</h3>
    </Box>
    <Grid container spacing={2} justifyContent={"center"}>
      {videos.map((video, index) => (
        <Grid item xs={12} sm={6} md={4}  key={index}>
          <OneVideo src={video.src!} alt={video.alt} />
        </Grid>
      ))}
    </Grid>
  </>
);